// @flow
import React from 'react';
import {
    FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import {
    Box, Button, RadioButton as GrommetRadioButton, Text,
} from 'grommet';
import { FieldWrapper } from 'speed-js-react-web';
import { point12Messages } from 'esm-web-pro/messages/points12Messages';
import { isEmpty } from 'lodash';
import memoizeOne from 'memoize-one';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';

type Props = {
    point: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    mapUuid: string,
    intl: IntlShape,
    onClose: () => void,
    history: { push: () => void },
    // eslint-disable-next-line react/no-unused-prop-types
    match: Object,
    connectCable: Function,
    id: string,
    title: Object,
};

type State = {
    checkedItemsKeys: Array<string>,
};


class Point11ConnectSidebar extends React.Component<Props, State> {
    memoizeFilteredLayers12: Object;

    memoizeOdfs: Object;

    constructor(props) {
        super(props);

        const { point } = props;
        const { connected_cables: { layers1_2: layers12 } } = point;
        const layers12Keys = Object.keys(layers12).filter((key) => layers12[key].type !== 'switch');

        this.state = {
            checkedItemsKeys: layers12Keys.length === 1 ? [layers12Keys[0]] : [],
        };
        this.memoizeFilteredLayers12 = memoizeOne(this.filteredLayers12);
        this.memoizeOdfs = memoizeOne(this.filteredOdfs);
    }

    componentDidUpdate(prevProps: $ReadOnly<Props>): void {
        const { point } = this.props;
        const { point: prevPoint } = prevProps;
        if (point !== prevPoint) {
            this.resetState();
        }
    }


    handleChange = (e) => {
        const { value } = e.target;

        if (e.target.checked && value) {
            this.setState({ checkedItemsKeys: [value] });
        } else {
            this.setState({ checkedItemsKeys: [value] });
        }
    };

    filteredLayers12 = (layers12: Object) => {
        const filteredLayers = [];

        Object.keys(layers12).forEach((key) => {
            if (layers12[key].type !== 'switch' && layers12[key].type !== 'odf') {
                filteredLayers.push(layers12[key]);
            }
        });

        return filteredLayers;
    };

    filteredOdfs = (layers12: Object) => {
        const odfs = [];

        Object.keys(layers12).forEach((key) => {
            if (layers12[key].type === 'odf') {
                odfs.push(layers12[key]);
            }
        });

        return odfs.sort((a, b) => Number(a.rack?.sequence) - Number(b.rack?.sequence));
    };

    renderContent = () => {
        const { point, intl: { formatMessage } } = this.props;
        const { connected_cables: { layers1_2: layers12 } } = point;
        const { checkedItemsKeys } = this.state;

        const filteredLayers12 = [...this.memoizeFilteredLayers12(layers12), ...this.memoizeOdfs(layers12)];

        return (
            <FieldWrapper label={(
                <FormattedMessage
                    id="content"
                    defaultMessage="Content"
                />
            )}
            >
                {filteredLayers12 && filteredLayers12.map((layer) => (
                    <Box key={`check-${layer.uuid}`} wrap margin="xsmall">
                        <GrommetRadioButton
                            name={`name-${layer.uuid}`}
                            checked={!!checkedItemsKeys.find((item) => item === layers12[layer.id].id.toString())}
                            onChange={this.handleChange}
                            value={layer.id}
                            label={layer.type === 'odf' ? (
                                <Text>
                                    {layer.rack
                                        ? `${formatMessage(point12Messages.rack)} ${layer.rack.sequence} `
                                        : ''}
                                    {`${
                                        formatMessage(point12Messages[layer.type])} ${
                                        layer.sequence}`}
                                </Text>
                            ) : (
                                <Text>
                                    {`${formatMessage(point12Messages[layer.type])} ${layer.sequence}`}
                                </Text>
                            )}
                        />
                        <Box key={layer.uuid} pad={{ top: 'small', left: 'large' }}>
                            {Object.keys(layer.cables).map((cableKey) => (
                                <Text key={cableKey}>
                                    {layer.cables[cableKey].ref}
                                </Text>
                            ))}
                        </Box>
                    </Box>
                ))}
            </FieldWrapper>
        );
    };

    resetState = () => {
        const { point } = this.props;
        const { connected_cables: { layers1_2: layers12 } } = point;

        const layers12Keys = this.memoizeFilteredLayers12(layers12).map((l) => l.id);
        this.setState({ checkedItemsKeys: layers12Keys.length === 1 ? [layers12Keys[0].toString()] : [] });
    };

    handleClose = () => {
        const { onClose } = this.props;
        onClose();
    };

    handleSave = () => {
        const {
            connectCable,
        } = this.props;
        const { checkedItemsKeys } = this.state;

        connectCable(checkedItemsKeys[0]);
    };

    render() {
        const {
            point, onClose, id, title,
        } = this.props;

        const { connected_cables: { layers1_2: layers12 } } = point;
        const filteredLayers12 = this.memoizeFilteredLayers12(layers12);
        const filteredOdfs = this.memoizeOdfs(layers12);

        return (
            <SidePane
                id={id}
                key={point.uuid}
                title={title}
            >
                <Box height="100%">
                    <Box
                        direction="row"
                        align="center"
                        height="100%"
                        overflow="auto"
                        style={{ position: 'unset' }}
                    >
                        <Box alignSelf="stretch" flex justify="between">
                            <Box>
                                <Box height="12px" flex={{ shrink: 0 }} />
                                <Box pad={{ horizontal: 'medium' }}>
                                    {this.renderContent()}
                                </Box>
                            </Box>
                            <Box height="12px" flex={{ shrink: 0 }} />
                            <Box>
                                {(!isEmpty(filteredLayers12) || !isEmpty(filteredOdfs)) ? (
                                    <ActionButtons
                                        onClose={this.handleClose}
                                        onSave={this.handleSave}
                                        submitButtonLabel={(
                                            <FormattedMessage
                                                id="point11-connect"
                                                defaultMessage="Connect"
                                            />
                                        )}
                                    />
                                )
                                    : (
                                        <Box fill="horizontal" pad="medium">
                                            <Button
                                                label={(
                                                    <FormattedMessage
                                                        id="point11-connect-close"
                                                        defaultMessage="Close"
                                                    />
                                                )}
                                                onClick={onClose}
                                                color="status-error"
                                            />
                                        </Box>
                                    )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </SidePane>
        );
    }
}

export default injectIntl(Point11ConnectSidebar);
